/**
 * Tab Registry — Heart of Science v2
 * Defines all 21 project lifecycle tabs with progressive unlock by pipeline stage
 * Last Updated: 2026-04-04
 */

import type { TabDefinition, LifecycleStage } from '../types'

/**
 * Complete registry of all 21 project lifecycle tabs
 * Ordered by first appearance in pipeline stages
 * Stage 'ALL' means tab is visible from INBOX onwards
 */
export const TAB_REGISTRY: TabDefinition[] = [
  // INBOX (2 tabs)
  { id: 'tab-quick-entry', label: 'Quick Entry', stage: 'INBOX' },
  { id: 'tab-documents', label: 'Documents', stage: 'ALL' },

  // LONG LIST (+1 = 3 tabs)
  { id: 'tab-overview', label: 'Overview', stage: 'LONG_LIST' },

  // IN REVIEW (+9 = 12 tabs)
  { id: 'tab-team', label: 'Project Team', stage: 'IN_REVIEW' },
  { id: 'tab-ip', label: 'IP & Transfer', stage: 'IN_REVIEW' },
  { id: 'tab-technology', label: 'Technology', stage: 'IN_REVIEW' },
  { id: 'tab-devphase', label: 'Dev Phase', stage: 'IN_REVIEW' },
  { id: 'tab-competition', label: 'Competition', stage: 'IN_REVIEW' },
  { id: 'tab-funding', label: 'Funding', stage: 'IN_REVIEW' },
  { id: 'tab-publications', label: 'Publications', stage: 'IN_REVIEW' },
  { id: 'tab-defensibility', label: 'Defensibility', stage: 'IN_REVIEW' },
  { id: 'tab-voting', label: 'Voting', stage: 'IN_REVIEW' },

  // EXTRA MILE (+4 = 16 tabs)
  { id: 'tab-outreach', label: 'Outreach & Validation', stage: 'EXTRA_MILE' },
  { id: 'tab-irl', label: 'IRL Assessment', stage: 'EXTRA_MILE' },
  { id: 'tab-scoring', label: 'Scoring', stage: 'EXTRA_MILE' },
  { id: 'tab-vb-recommendation', label: 'VB Recommendation', stage: 'EXTRA_MILE' },

  // VB PROCESS (+4 = 20 tabs)
  { id: 'tab-vbteam', label: 'VB Team', stage: 'VB_PROCESS' },
  { id: 'tab-termsheet', label: 'Term Sheet', stage: 'VB_PROCESS' },
  { id: 'tab-venturebuilding', label: 'Venture Building', stage: 'VB_PROCESS' },
  { id: 'tab-fundraising', label: 'Fundraising Readiness', stage: 'VB_PROCESS' },

  // FUNDED (+1 = 21 tabs)
  { id: 'tab-funded', label: 'Funded', stage: 'FUNDED' },
]

/**
 * Stage ordering for progressive unlock
 * Higher number = more advanced stage, unlocks more tabs
 * ALL (–1) = visible at any stage
 */
const STAGE_ORDER: Record<LifecycleStage, number> = {
  INBOX: 0,
  LONG_LIST: 1,
  IN_REVIEW: 2,
  EXTRA_MILE: 3,
  VB_PROCESS: 4,
  FUNDED: 5,
  ALL: -1, // Always visible
}

/**
 * Returns visible tabs for a given pipeline stage
 * Includes all tabs from current stage, previous stages, and ALL tabs
 * @param currentStage - Current pipeline stage code
 * @returns Array of TabDefinition objects visible at this stage
 */
export function getVisibleTabs(currentStage: string): TabDefinition[] {
  const stageLevel = STAGE_ORDER[currentStage as LifecycleStage] ?? 0

  const tabs = TAB_REGISTRY.filter(tab => {
    if (tab.stage === 'ALL') return true
    const tabLevel = STAGE_ORDER[tab.stage]
    return tabLevel <= stageLevel
  })

  // Documents always last
  const docsIdx = tabs.findIndex(t => t.id === 'tab-documents')
  if (docsIdx > -1) {
    const [docsTab] = tabs.splice(docsIdx, 1)
    tabs.push(docsTab)
  }

  return tabs
}

/**
 * Tab completion status for traffic light indicators
 * 'empty' = red, 'partial' = orange, 'complete' = green, 'none' = no indicator
 */
export type TabStatus = 'empty' | 'partial' | 'complete' | 'none'

/**
 * Compute tab completion status from project detail data
 * Simple heuristic: checks key fields for each tab
 */
export function getTabStatuses(
  project: { description: string | null; trl: number | null; domain_profile: Record<string, unknown>; score_composite: number | null },
  response: {
    researchers: unknown[]
    funding: unknown[]
    files: unknown[]
    sources: unknown[]
    competitive_checks: unknown[]
    competitors: unknown[]
    scores: unknown[]
    votes: unknown[]
    interactions: unknown[]
  },
): Record<string, TabStatus> {
  const dp = project.domain_profile || {}
  const dpKeys = Object.keys(dp).filter(k => dp[k] !== null && dp[k] !== '' && dp[k] !== undefined)

  // Helper: check if domain_profile has any keys from a list
  const hasProfileFields = (...fields: string[]) => fields.some(f => dp[f] !== null && dp[f] !== '' && dp[f] !== undefined)
  const allProfileFields = (...fields: string[]) => fields.every(f => dp[f] !== null && dp[f] !== '' && dp[f] !== undefined)

  return {
    'tab-quick-entry': project.description && project.trl !== null
      ? 'complete'
      : project.description || project.trl !== null
        ? 'partial'
        : 'empty',

    'tab-documents': response.files.length > 0 ? 'complete' : 'none',

    'tab-overview': dpKeys.length >= 8
      ? 'complete'
      : dpKeys.length > 0
        ? 'partial'
        : 'empty',

    'tab-team': 'none', // team assessed separately via assignments

    'tab-ip': hasProfileFields('ip_status', 'patent_count', 'license_type')
      ? allProfileFields('ip_status', 'patent_count', 'license_type') ? 'complete' : 'partial'
      : 'empty',

    'tab-technology': hasProfileFields('technology_readiness', 'technology_description')
      ? 'partial'
      : project.trl !== null ? 'partial' : 'empty',

    'tab-devphase': project.trl !== null ? 'complete' : 'empty',

    'tab-competition': response.competitive_checks.length > 0 || response.competitors.length > 0
      ? response.competitors.length > 0 ? 'complete' : 'partial'
      : 'empty',

    'tab-funding': response.funding.length > 0 ? 'complete' : 'empty',

    'tab-publications': response.interactions.length > 0 ? 'complete' : 'empty',

    'tab-defensibility': hasProfileFields('defensibility_moat', 'defensibility_score')
      ? 'complete' : 'empty',

    'tab-voting': response.votes.length > 0 ? 'complete' : 'empty',

    'tab-outreach': 'empty',
    'tab-irl': 'empty',
    'tab-scoring': response.scores.length > 0 ? 'complete' : 'empty',
    'tab-vb-recommendation': 'empty',
    'tab-vbteam': 'empty',
    'tab-termsheet': 'empty',
    'tab-venturebuilding': 'empty',
    'tab-fundraising': 'empty',
    'tab-funded': 'empty',
  }
}

/**
 * Stage badge colors for tab header display
 * Each stage has a background (15% opacity) and text color
 */
export const STAGE_BADGE_COLORS: Record<
  LifecycleStage,
  { bg: string; text: string }
> = {
  INBOX: {
    bg: 'rgba(230, 27, 37, 0.15)',
    text: '#E61B25',
  },
  LONG_LIST: {
    bg: 'rgba(59, 130, 246, 0.15)',
    text: '#3B82F6',
  },
  IN_REVIEW: {
    bg: 'rgba(139, 92, 246, 0.15)',
    text: '#8B5CF6',
  },
  EXTRA_MILE: {
    bg: 'rgba(245, 158, 11, 0.15)',
    text: '#F59E0B',
  },
  VB_PROCESS: {
    bg: 'rgba(16, 185, 129, 0.15)',
    text: '#10B981',
  },
  FUNDED: {
    bg: 'rgba(34, 197, 94, 0.15)',
    text: '#22C55E',
  },
  ALL: {
    bg: 'var(--color-bg-surface)',
    text: 'var(--color-text-tertiary)',
  },
}

/**
 * Get color config for a specific stage
 * @param stage - LifecycleStage
 * @returns Color pair { bg, text }
 */
export function getStageBadgeColors(
  stage: LifecycleStage,
): { bg: string; text: string } {
  return STAGE_BADGE_COLORS[stage] || STAGE_BADGE_COLORS.ALL
}

/**
 * Helper: find a tab definition by ID
 * @param tabId - Tab ID string
 * @returns TabDefinition or undefined
 */
export function getTabById(tabId: string): TabDefinition | undefined {
  return TAB_REGISTRY.find(tab => tab.id === tabId)
}

/**
 * Helper: get stage for a tab
 * @param tabId - Tab ID string
 * @returns LifecycleStage or undefined
 */
export function getTabStage(tabId: string): LifecycleStage | undefined {
  return getTabById(tabId)?.stage
}
